import React, { useState, useEffect } from 'react';
import { 
  Star, Gift, Mail, CheckCircle, Clock, Pause, Play, 
  Settings, Users, TrendingUp, ExternalLink, Plus, RefreshCw,
  ChevronDown, ChevronUp, MoreVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ReviewsReferrals = () => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  
  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    google_review_url: '',
    facebook_review_url: '',
    yelp_review_url: '',
    auto_trigger_on_completion: true
  });

  const token = localStorage.getItem('rooftrack_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [campaignsRes, referralsRes, statsRes, settingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reviews/campaigns`, { headers }),
        axios.get(`${BACKEND_URL}/api/reviews/referrals`, { headers }),
        axios.get(`${BACKEND_URL}/api/reviews/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/reviews/settings`, { headers })
      ]);
      
      setCampaigns(campaignsRes.data.campaigns || []);
      setReferrals(referralsRes.data.referrals || []);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      setSettingsForm({
        google_review_url: settingsRes.data.google_review_url || '',
        facebook_review_url: settingsRes.data.facebook_review_url || '',
        yelp_review_url: settingsRes.data.yelp_review_url || '',
        auto_trigger_on_completion: settingsRes.data.auto_trigger_on_completion ?? true
      });
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePauseCampaign = async (campaignId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/reviews/campaigns/${campaignId}/pause`, {}, { headers });
      toast.success('Campaign paused');
      fetchData();
    } catch (err) {
      toast.error('Failed to pause campaign');
    }
  };

  const handleResumeCampaign = async (campaignId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/reviews/campaigns/${campaignId}/resume`, {}, { headers });
      toast.success('Campaign resumed');
      fetchData();
    } catch (err) {
      toast.error('Failed to resume campaign');
    }
  };

  const handleMarkReviewReceived = async (campaignId, platform) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/reviews/campaigns/${campaignId}/mark-received`,
        { platform },
        { headers }
      );
      toast.success('Review marked as received');
      fetchData();
    } catch (err) {
      toast.error('Failed to mark review');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/reviews/settings`, settingsForm, { headers });
      toast.success('Settings saved');
      setShowSettings(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status, reviewReceived) => {
    if (reviewReceived) {
      return <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">Review Received</span>;
    }
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">Active</span>;
      case 'paused':
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Paused</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">Completed</span>;
      default:
        return <span className="px-2 py-1 bg-slate-500/20 text-slate-400 text-xs rounded-full">{status}</span>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Star className="w-7 h-7 text-yellow-400" />
            Reviews & Referrals
          </h1>
          <p className="text-slate-400 mt-1">Collect reviews and referrals from your customers</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowSettings(!showSettings)}
            className="border-slate-600"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button onClick={fetchData} variant="ghost" className="text-slate-400">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Review Platform Settings</h3>
          <p className="text-slate-400 text-sm mb-6">
            Add your business review page URLs. These links will be sent to customers in review request emails.
          </p>
          
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Google Business Review URL</label>
              <Input
                value={settingsForm.google_review_url}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, google_review_url: e.target.value }))}
                placeholder="https://g.page/r/..."
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Facebook Review URL</label>
              <Input
                value={settingsForm.facebook_review_url}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, facebook_review_url: e.target.value }))}
                placeholder="https://facebook.com/..."
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Yelp Review URL</label>
              <Input
                value={settingsForm.yelp_review_url}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, yelp_review_url: e.target.value }))}
                placeholder="https://yelp.com/biz/..."
                className="bg-slate-700 border-slate-600"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-3 text-slate-300">
              <input
                type="checkbox"
                checked={settingsForm.auto_trigger_on_completion}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, auto_trigger_on_completion: e.target.checked }))}
                className="rounded bg-slate-700 border-slate-600"
              />
              Automatically start review campaign when project is marked complete
            </label>
            <Button onClick={handleSaveSettings} className="bg-blue-600 hover:bg-blue-700">
              Save Settings
            </Button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
              <Mail className="w-4 h-4" />
              Active Campaigns
            </div>
            <div className="text-2xl font-bold text-white">{stats.reviews?.active_campaigns || 0}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
              <Star className="w-4 h-4 text-yellow-400" />
              Reviews Received
            </div>
            <div className="text-2xl font-bold text-white">{stats.reviews?.reviews_received || 0}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
              <TrendingUp className="w-4 h-4 text-green-400" />
              Conversion Rate
            </div>
            <div className="text-2xl font-bold text-white">{stats.reviews?.conversion_rate || 0}%</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
              <Gift className="w-4 h-4 text-emerald-400" />
              Referrals Received
            </div>
            <div className="text-2xl font-bold text-white">{stats.referrals?.total_referrals || 0}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700">
        <button
          onClick={() => setActiveTab('campaigns')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'campaigns'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Review Campaigns ({campaigns.length})
        </button>
        <button
          onClick={() => setActiveTab('referrals')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'referrals'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Referrals ({referrals.length})
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      ) : activeTab === 'campaigns' ? (
        <div className="space-y-4">
          {campaigns.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No review campaigns yet</p>
              <p className="text-sm mt-1">Campaigns will appear here when you complete projects</p>
            </div>
          ) : (
            campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="bg-slate-800 rounded-xl border border-slate-700 p-5"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-semibold">{campaign.customer_name}</h3>
                      {getStatusBadge(campaign.status, campaign.review_received)}
                    </div>
                    <p className="text-slate-400 text-sm">{campaign.property_address}</p>
                    <p className="text-slate-500 text-xs mt-1">
                      Started: {formatDate(campaign.created_at)} • Emails sent: {campaign.emails_sent_count}/{campaign.emails?.length || 4}
                    </p>
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-slate-400">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                      {campaign.status === 'active' && !campaign.review_received && (
                        <DropdownMenuItem onClick={() => handlePauseCampaign(campaign.id)}>
                          <Pause className="w-4 h-4 mr-2" />
                          Pause Campaign
                        </DropdownMenuItem>
                      )}
                      {campaign.status === 'paused' && (
                        <DropdownMenuItem onClick={() => handleResumeCampaign(campaign.id)}>
                          <Play className="w-4 h-4 mr-2" />
                          Resume Campaign
                        </DropdownMenuItem>
                      )}
                      {!campaign.review_received && (
                        <>
                          <DropdownMenuItem onClick={() => handleMarkReviewReceived(campaign.id, 'google')}>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark Google Review Received
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleMarkReviewReceived(campaign.id, 'facebook')}>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark Facebook Review Received
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleMarkReviewReceived(campaign.id, 'yelp')}>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark Yelp Review Received
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuItem onClick={() => window.open(`/review/${campaign.id}`, '_blank')}>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Customer Page
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Email Progress */}
                {campaign.emails && campaign.emails.length > 0 && (
                  <div className="mt-4 flex gap-2">
                    {campaign.emails.map((email, idx) => (
                      <div
                        key={idx}
                        className={`flex-1 h-2 rounded-full ${
                          email.status === 'sent' ? 'bg-green-500' :
                          email.status === 'pending' ? 'bg-slate-600' :
                          email.status === 'skipped' ? 'bg-slate-700' :
                          'bg-red-500'
                        }`}
                        title={`Day ${email.day}: ${email.status}`}
                      />
                    ))}
                  </div>
                )}

                {campaign.review_received && (
                  <div className="mt-4 flex items-center gap-2 text-green-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Review received on {campaign.review_platform} • {formatDate(campaign.review_received_date)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {referrals.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Gift className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No referrals yet</p>
              <p className="text-sm mt-1">Referrals from customers will appear here</p>
            </div>
          ) : (
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-slate-400 font-medium">Referred Person</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Contact</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Referred By</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Date</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((referral, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50">
                      <td className="p-4">
                        <div className="text-white font-medium">{referral.referred_name}</div>
                        {referral.referred_address && (
                          <div className="text-slate-400 text-sm">{referral.referred_address}</div>
                        )}
                      </td>
                      <td className="p-4 text-slate-300">
                        <div>{referral.referred_email || '-'}</div>
                        <div className="text-sm text-slate-400">{referral.referred_phone || '-'}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-slate-300">{referral.referrer_name}</div>
                        <div className="text-sm text-slate-400">{referral.referrer_email}</div>
                      </td>
                      <td className="p-4 text-slate-400">{formatDate(referral.created_at)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          referral.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                          referral.status === 'contacted' ? 'bg-yellow-500/20 text-yellow-400' :
                          referral.status === 'converted' ? 'bg-green-500/20 text-green-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {referral.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewsReferrals;
